"use client";
import React from "react";
import { Button, CircularProgress, TextField } from "@mui/material";
import { useRouter } from "next/navigation";
import { useFormik } from "formik";
import * as Yup from "yup"; // Yup for validation
import { setCookie } from "cookies-next";
import { login } from "@/app/services/auth";
import toast from "react-hot-toast";
import { IResponse, IResponseError } from "@/types";
import { CONSTANTS } from "@/app/constants";

// Validation schema using Yup
const validationSchema = Yup.object({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  password: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
});

const LoginForm = () => {
  const [isLoading, setIsLoading] = React.useState(false); // Loading state for the loader
  const router = useRouter();

  // useFormik hook to manage form state and validation
  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema,
    onSubmit: (values) => {
      setIsLoading(true);
      login(values)
        .then((res: IResponse) => {
          setCookie(CONSTANTS.JWT_COOKIE_NAME, res?.data?.token);
          router.push("/admin/dashboard");
        })
        .catch((err: IResponseError) => {
          toast.error(err?.error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
  });

  return (
    <form
      onSubmit={formik.handleSubmit}
      className="space-y-4"
    >
      <div className="flex flex-col gap-y-[23px]">
        {/* Email Field */}
        <TextField
          label="Email address"
          type="email"
          name="email"
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder="Enter email address"
          fullWidth
          variant="filled"
          size="medium"
          focused={true}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
        />

        {/* Password Field */}
        <TextField
          label="Password"
          type="password"
          name="password"
          value={formik.values.password}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder="Enter password"
          fullWidth
          variant="filled"
          size="medium"
          focused={true}
          error={formik.touched.password && Boolean(formik.errors.password)}
          helperText={formik.touched.password && formik.errors.password}
        />
      </div>

      {/* Submit Button */}
      <div className="text-end">
        <Button
          type="submit"
          variant="contained"
          className="!normal-case !rounded-[40px] text-center px-6 py-2 !text-black text-[14px] tracking-[0.1px] !font-bold !bg-primary-500 disabled:!bg-primary-600"
          disabled={isLoading} // Disable button during loading
          endIcon={
            isLoading ? (
              <CircularProgress
                size={20}
                color="inherit"
              />
            ) : null
          }
        >
          Login
        </Button>
      </div>
    </form>
  );
};

export default LoginForm;
